import Image from '@/app/common/components/Image'
import { formatPhoneNumber } from '@/app/common/utilities'
import { Card, CardContent, CardHeader } from '@/common/components/Card.tsx'
import { currencyFormatter } from '@/lib/utils'
import { ProviderDetail } from '@/schemas/provider.schema.ts'
import { SchoolResponseSchema } from '@/schemas/school-application.schema'

interface Props {
  school_info: SchoolResponseSchema
  provider_info: ProviderDetail
}
function SchoolInfo(props: Props) {
  const { school_info, provider_info } = props

  const formatTuition = (value: string | null) => {
    if (!value) {
      return 'Not specified'
    }

    const valueAsNumber = Number(value)
    if (isNaN(valueAsNumber) || valueAsNumber < 0) {
      return 'Not specified'
    }
    return currencyFormatter.format(valueAsNumber)
  }

  // Improved helper for demographics percentage
  const getDemographicsPercentage = (
    value: string | number | null | undefined
  ) => {
    if (value === null || value === undefined) return 'Not specified'
    if (typeof value === 'number') {
      return isNaN(value) ? 'Not specified' : `${value}%`
    }
    if (typeof value === 'string') {
      const trimmed = value.trim()
      if (trimmed === '') return 'Not specified'
      // If it's a valid number string, show as percentage
      const num = Number(trimmed)
      if (!isNaN(num)) return `${num}%`
      // Otherwise, just return the string (for edge cases)
      return trimmed
    }
    return 'Not specified'
  }

  const getTextValue = (value: string | null | number) => {
    if (typeof value === 'number') {
      return value
    }

    return value && value?.trim().length > 0 ? value : 'Not specified'
  }

  // Left column cards
  const leftColumn = [
    // School Basic Information
    <Card key="school-info" className="h-fit w-full">
      <CardHeader>
        <h3 className="text-lg font-medium">School Information</h3>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="grid grid-cols-1 space-y-3">
            {school_info?.school_picture && (
              <div>
                <Image
                  className="h-20 w-20 object-cover rounded-full"
                  path={school_info.school_picture}
                />
              </div>
            )}
            <div>
              <p className="text-sm text-gray-500">School Name</p>
              <p className="font-semibold text-lg">
                {getTextValue(school_info.school_name)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Physical Address</p>
              <div className="space-y-1">
                <p>{getTextValue(provider_info.address)}</p>
                {school_info.street_address_line_2 && (
                  <p>{getTextValue(school_info.street_address_line_2)}</p>
                )}
                <p>
                  {getTextValue(provider_info.city)},{' '}
                  {getTextValue(provider_info.global_state_name)}{' '}
                  {getTextValue(provider_info.zipcode)}
                </p>
              </div>
            </div>
            <div>
              <p className="text-sm text-gray-500">Mailing Address</p>
              <div className="space-y-1">
                <p>{getTextValue(provider_info.mailing_address)}</p>
                <p>
                  {provider_info.mailing_address_line_2 && (
                    <p>{getTextValue(provider_info.mailing_address_line_2)}</p>
                  )}
                </p>
                <p>
                  {getTextValue(provider_info.mailing_city)},{' '}
                  {getTextValue(provider_info.mailing_global_state_name)}{' '}
                  {getTextValue(provider_info.mailing_zip_code)}
                </p>
              </div>
            </div>

            <div>
              <p className="text-sm text-gray-500">Year Established</p>
              <p>{school_info.year_established}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Co-ed Status </p>
              <p>{getTextValue(school_info.coed_status)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Hybrid Status </p>
              <p> {getTextValue(school_info.hybrid_status)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">
                In what area is your school located?
              </p>
              <p>{getTextValue(school_info.school_area_location)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Lowest Grade Served</p>
              <p>{getTextValue(school_info.lowest_grade_served)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Highest Grade Served</p>
              <p>{getTextValue(school_info.highest_grade_served)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Religious Affiliation</p>
              <p>{getTextValue(school_info.religious_affiliation)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Current Enrollment</p>
              <p>{getTextValue(school_info.current_enrollment)} </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Maximum School Capacity</p>
              <p>{getTextValue(school_info.maximum_school_capacity)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">
                Students per teacher (Full Time){' '}
              </p>
              <p>{getTextValue(school_info.student_teacher_ratio_full_time)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">
                Students per teacher (Full Time and Part Time){' '}
              </p>
              <p>{getTextValue(school_info.student_teacher_ratio_all)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Website</p>
              <p>{getTextValue(school_info.school_website)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">
                Alternative online source (Ex. social media){' '}
              </p>
              <p>{getTextValue(school_info.online_information_source)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">
                What percentage of your students are 350% or less of Federal
                Poverty Guidelines?
              </p>
              <p>
                {getDemographicsPercentage(
                  school_info.percentage_350_poverty_guidelines
                )}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">
                How many additional students with family income 350% or less of
                Federal Poverty Guidelines would you like to accommodate in the
                coming school year?
              </p>
              <p>
                {getDemographicsPercentage(
                  school_info.additional_students_350_poverty
                )}
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>,
    // Tuition & Financial Information
    <Card key="tuition-info" className="h-fit w-full">
      <CardHeader>
        <h3 className="text-lg font-medium">Tuition & Financial</h3>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="grid grid-cols-1 space-y-3">
            <div>
              <p className="text-sm text-gray-500">Charges Tuition</p>
              <p>{getTextValue(school_info.charges_tuition)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">
                Pre-K Annual Non-Discounted Tuition
              </p>
              <p>{formatTuition(school_info.pre_k_tuition)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">
                Kindergarten Annual Non-Discounted Tuition
              </p>
              <p>{formatTuition(school_info.kindergarten_tuition)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">
                Elementary Annual Non-Discounted Tuition
              </p>
              <p>{formatTuition(school_info.elementary_tuition)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">
                Middle School Annual Non-Discounted Tuition
              </p>
              <p>{formatTuition(school_info.middle_school_tuition)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">
                High School Annual Non-Discounted Tuition
              </p>
              <p>{formatTuition(school_info.high_school_tuition)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">
                Other forms of variable tuition
              </p>
              <p>{getTextValue(school_info.variable_tuition_forms)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">
                Per Pupil Expenditures (Cost to Educate){' '}
              </p>
              <p>{formatTuition(school_info.per_pupil_expenditures)}</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>,
    // Accreditation & Testing
    <Card key="accreditation-info" className="h-fit w-full">
      <CardHeader>
        <h3 className="text-lg font-medium">Accreditation & Testing</h3>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="grid grid-cols-1 space-y-3">
            <div>
              <p className="text-sm text-gray-500">Accreditation Bodies</p>
              <p>{getTextValue(school_info.accreditation_bodies)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">
                Administers Standardized Tests
              </p>
              <p>{getTextValue(school_info.administers_standardized_test)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">
                Standardized Tests Administered
              </p>
              <p>{getTextValue(school_info.standardized_tests_administered)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">
                Student Assessment Methods
              </p>
              <p>{getTextValue(school_info.student_assessment_methods)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">
                School contracted with FACTS for grant and aid management
              </p>
              <p>{getTextValue(school_info.contracted_with_facts)}</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>,
  ]

  // Right column cards
  const rightColumn = [
    // Contact Information
    <Card key="contact-info" className="h-fit w-full">
      <CardHeader>
        <h3 className="text-lg font-medium">Contact Information</h3>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="grid grid-cols-1 space-y-3">
            <div>
              <p className="text-sm text-gray-500">Primary Contact</p>
              <p>
                {getTextValue(provider_info.fpc_first_name)}{' '}
                {getTextValue(provider_info.fpc_last_name)}
              </p>
            </div>
            {/*  <div>
              <p className="text-sm text-gray-500">Title</p>
              <p>{getTextValue(provider_info.fpc_)}</p>
            </div> */}
            <div>
              <p className="text-sm text-gray-500">Phone</p>
              <p>
                {provider_info.fpc_phone_number
                  ? formatPhoneNumber(provider_info.fpc_phone_number)
                  : 'Not specified'}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Email</p>
              <p>{getTextValue(provider_info.fpc_email)}</p>
            </div>
            {/*  <div>
              <p className="text-sm text-gray-500">Role</p>
              <p>{getTextValue(provider_info.)}</p>
            </div> */}
          </div>
        </div>
      </CardContent>
    </Card>,
    // Additional Information
    <Card key="additional-info" className="h-fit w-full">
      <CardHeader>
        <h3 className="text-lg font-medium">Additional Information</h3>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="grid grid-cols-1 space-y-3">
            <div>
              <p className="text-sm text-gray-500">Areas of Specialization</p>
              <p>{getTextValue(school_info.areas_of_specialization)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">
                Learning Differences Accommodated
              </p>
              <p>
                {getTextValue(school_info.learning_differences_accommodated)}
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">
                Additional Programs Offered
              </p>
              <p>{getTextValue(school_info.additional_programs_offered)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Mission Statement</p>
              <p className="italic">
                "{getTextValue(school_info.mission_statement)}"
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-500">History of School</p>
              <p className="">
                "{getTextValue(school_info.history_of_school)}"
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>,
    // Demographics
    <Card key="demographics-info" className="h-fit w-full">
      <CardHeader>
        <h3 className="text-lg font-medium">Demographics</h3>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Gender Distribution */}
          <div className="space-y-3">
            <p className="text-sm text-gray-500 font-medium">
              Gender Distribution
            </p>
            <div className="grid grid-cols-1 space-y-2">
              <div className="flex justify-between">
                <span>Female:</span>
                <span className="font-medium">
                  {getDemographicsPercentage(school_info.gender_female)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Male:</span>
                <span className="font-medium">
                  {getDemographicsPercentage(school_info.gender_male)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Other:</span>
                <span className="font-medium">
                  {getDemographicsPercentage(school_info.gender_other)}
                </span>
              </div>
            </div>
          </div>
          {/* ESL Information */}
          <div className="space-y-3">
            <p className="text-sm text-gray-500 font-medium">ESL Information</p>
            <div className="grid grid-cols-1 space-y-2">
              <div className="flex justify-between">
                <span>ESL Students:</span>
                <span className="font-medium">
                  {getDemographicsPercentage(
                    school_info.percentage_esl_students
                  )}
                </span>
              </div>
              <div className="flex justify-between">
                <span>ESL Parents:</span>
                <span className="font-medium">
                  {getDemographicsPercentage(
                    school_info.percentage_esl_parents
                  )}
                </span>
              </div>
            </div>
          </div>
          <div className="space-y-3">
            <p className="text-sm text-gray-500 font-medium">
              Ethnicity Distribution
            </p>
            <div className="grid grid-cols-1 space-y-2">
              <div className="flex justify-between">
                <span>African American:</span>
                <span className="font-medium">
                  {getDemographicsPercentage(
                    school_info.ethnicity_african_american
                  )}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Asian:</span>
                <span className="font-medium">
                  {getDemographicsPercentage(school_info.ethnicity_asian)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Caucasian:</span>
                <span className="font-medium">
                  {getDemographicsPercentage(school_info.ethnicity_caucasian)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>East Asian:</span>
                <span className="font-medium">
                  {getDemographicsPercentage(school_info.ethnicity_east_asian)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Hispanic/Latino:</span>
                <span className="font-medium">
                  {getDemographicsPercentage(
                    school_info.ethnicity_hispanic_latino
                  )}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Middle Eastern:</span>
                <span className="font-medium">
                  {getDemographicsPercentage(
                    school_info.ethnicity_middle_eastern
                  )}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Multiracial:</span>
                <span className="font-medium">
                  {getDemographicsPercentage(school_info.ethnicity_multiracial)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Native American:</span>
                <span className="font-medium">
                  {getDemographicsPercentage(
                    school_info.ethnicity_native_american
                  )}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Native Hawaiian:</span>
                <span className="font-medium">
                  {getDemographicsPercentage(
                    school_info.ethnicity_native_hawaiian
                  )}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Prefer Not to Say:</span>
                <span className="font-medium">
                  {getDemographicsPercentage(
                    school_info.ethnicity_prefer_not_to_say
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>,
    // ACE Program
    <Card key="ace-info" className="h-fit w-full">
      <CardHeader>
        <h3 className="text-lg font-medium">ACE Program</h3>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="grid grid-cols-1 space-y-3">
            <div>
              <p className="text-sm text-gray-500">New School to ACE</p>
              <p>{getTextValue(school_info.new_school_to_ace)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Contracted with FACTS</p>
              <p>{getTextValue(school_info.contracted_with_facts)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Receives ACH Payment</p>
              <p>{getTextValue(school_info.receives_ach_payment)}</p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>,
  ]

  return (
    <>
      <div className="flex gap-4 mt-8 flex-nowrap w-full max-w-full">
        <div className="flex flex-col gap-4 w-1/2 min-w-0 max-w-1/2">
          {leftColumn}
        </div>
        <div className="flex flex-col gap-4 w-1/2 min-w-0 max-w-1/2">
          {rightColumn}
        </div>
      </div>
    </>
  )
}

export default SchoolInfo
